import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, Switch, message, Space, Divider, Row as AntRow, Col, Button } from 'antd';
import { Voucher, UpdateVoucherDto, DiscountType } from '../../common/voucher.interface';
import { 
    GiftOutlined, 
    DollarOutlined, 
    CalendarOutlined, 
    EditOutlined, 
    CloseOutlined,
    FileTextOutlined,
    UserOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { confirmCloseModal } from '../modalEditBook/CModalConfirmClose';
import dayjs from 'dayjs';

interface CModalEditVoucherProps {
  visible: boolean;
  voucher: Voucher | null;
  onCancel: () => void;
  onOk: (data: UpdateVoucherDto) => void;
}

const CModalEditVoucher: React.FC<CModalEditVoucherProps> = ({
  visible,
  voucher,
  onCancel,
  onOk,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [voucherType, setVoucherType] = useState<'FREE' | 'PAID'>('FREE');
  const [hasChanges, setHasChanges] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (voucher && visible) {
      form.setFieldsValue({
        code: voucher.code,
        name: voucher.name,
        description: voucher.description,
        type: voucher.type,
        discount: voucher.discount,
        maxDiscount: voucher.maxDiscount,
        minOrder: voucher.minOrder,
        pointsCost: voucher.pointsCost,
        quantity: voucher.quantity,
        startDate: dayjs(voucher.startDate),
        endDate: dayjs(voucher.endDate),
        isActive: voucher.isActive,
      });
      setVoucherType(voucher.type || 'FREE');
      setHasChanges(false);
    }
  }, [voucher, visible, form]);

  useEffect(() => {
    if (voucher) {
      const currentValues = form.getFieldsValue();
      const hasFormChanges = 
        currentValues.code !== voucher.code ||
        currentValues.name !== voucher.name ||
        currentValues.description !== voucher.description ||
        currentValues.type !== voucher.type ||
        currentValues.discount !== voucher.discount ||
        currentValues.maxDiscount !== voucher.maxDiscount ||
        currentValues.minOrder !== voucher.minOrder ||
        currentValues.pointsCost !== voucher.pointsCost ||
        currentValues.quantity !== voucher.quantity ||
        !dayjs(currentValues.startDate).isSame(dayjs(voucher.startDate)) ||
        !dayjs(currentValues.endDate).isSame(dayjs(voucher.endDate)) ||
        currentValues.isActive !== voucher.isActive;
      
      setHasChanges(hasFormChanges);
    }
  }, [values, voucher]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const voucherData: UpdateVoucherDto = {
        title: values.name,
        description: values.description,
        discountValue: values.discount,
        maxDiscountAmount: values.maxDiscount,
        usageLimit: values.quantity,
        isActive: values.isActive,
      };

      onOk(voucherData);
    } catch (error) {
      message.error('Vui lòng kiểm tra lại thông tin');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    confirmCloseModal({
      hasChanges,
      onConfirm: () => {
        resetForm();
        onCancel();
      },
      title: 'Xác nhận đóng',
      content: 'Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng modal chỉnh sửa voucher?'
    });
  };

  const resetForm = () => {
    form.resetFields();
    setVoucherType('FREE');
    setHasChanges(false);
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      title={
        <Space>
          <EditOutlined />
          <span>Chỉnh sửa voucher</span>
        </Space>
      }
      className="voucher-edit-modal"
      footer={null}
      width={700}
      destroyOnClose
      maskClosable={true}
    >
      <div className="modal-content">
        <Form
          form={form}
          layout="vertical"
          className="voucher-form"
          initialValues={{
            type: 'FREE',
            isActive: true,
          }}
          onFinish={handleOk}
        >
          <AntRow gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <GiftOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mã voucher</span>
                  </Space>
                }
                name="code"
                rules={[
                  { required: true, message: 'Vui lòng nhập mã voucher' },
                  { min: 3, message: 'Mã voucher phải có ít nhất 3 ký tự' },
                ]}
              >
                <Input placeholder="Nhập mã voucher" className="form-input" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Tên voucher</span>
                  </Space>
                }
                name="name"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên voucher' },
                ]}
              >
                <Input placeholder="Nhập tên voucher" className="form-input" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mô tả</span>
                  </Space>
                }
                name="description"
                rules={[
                  { required: true, message: 'Vui lòng nhập mô tả' },
                ]}
              >
                <Input.TextArea 
                  rows={3} 
                  placeholder="Nhập mô tả voucher" 
                  className="form-textarea"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Loại voucher</span>
                  </Space>
                }
                name="type"
                rules={[
                  { required: true, message: 'Vui lòng chọn loại voucher' },
                ]}
              >
                <Select
                  placeholder="Chọn loại voucher"
                  onChange={(value) => setVoucherType(value)}
                  className="form-select"
                >
                  <Select.Option value="FREE">Miễn phí</Select.Option>
                  <Select.Option value="PAID">Trả phí</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Phần trăm giảm giá (%)</span>
                  </Space>
                }
                name="discount"
                rules={[
                  { required: true, message: 'Vui lòng nhập phần trăm giảm giá' },
                  { type: 'number', min: 1, max: 100, message: 'Phần trăm giảm giá phải từ 1-100%' },
                ]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  placeholder="Nhập phần trăm giảm giá"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Giảm tối đa (VND)</span>
                  </Space>
                }
                name="maxDiscount"
                rules={[
                  { required: true, message: 'Vui lòng nhập giảm tối đa' },
                  { type: 'number', min: 0, message: 'Giảm tối đa phải lớn hơn 0' },
                ]}
              >
                <InputNumber
                  min={0}
                  placeholder="Nhập giảm tối đa"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Đơn hàng tối thiểu (VND)</span>
                  </Space>
                }
                name="minOrder"
                rules={[
                  { required: true, message: 'Vui lòng nhập đơn hàng tối thiểu' },
                  { type: 'number', min: 0, message: 'Đơn hàng tối thiểu phải lớn hơn 0' },
                ]}
              >
                <InputNumber
                  min={0}
                  placeholder="Nhập đơn hàng tối thiểu"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            {voucherType === 'PAID' && (
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Điểm cần thiết</span>
                    </Space>
                  }
                  name="pointsCost"
                  rules={[
                    { required: true, message: 'Vui lòng nhập điểm cần thiết' },
                    { type: 'number', min: 1, message: 'Điểm cần thiết phải lớn hơn 0' },
                  ]}
                >
                  <InputNumber
                    min={1}
                    placeholder="Nhập điểm cần thiết"
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Số lượng</span>
                  </Space>
                }
                name="quantity"
                rules={[
                  { required: true, message: 'Vui lòng nhập số lượng' },
                  { type: 'number', min: 1, message: 'Số lượng phải lớn hơn 0' },
                ]}
              >
                <InputNumber
                  min={1}
                  placeholder="Nhập số lượng"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Ngày bắt đầu</span>
                  </Space>
                }
                name="startDate"
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày bắt đầu' },
                ]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Chọn ngày bắt đầu"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Ngày kết thúc</span>
                  </Space>
                }
                name="endDate"
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày kết thúc' },
                ]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Chọn ngày kết thúc"
                  className="form-input"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <SettingOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Trạng thái</span>
                  </Space>
                }
                name="isActive"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tạm dừng" />
              </Form.Item>
            </Col>
          </AntRow>

          <Divider style={{ margin: '16px 0' }} />

          <div className="modal-actions">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              icon={<CloseOutlined />}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="save-button"
              icon={<EditOutlined />}
              loading={loading}
            >
              Cập nhật
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CModalEditVoucher; 